/*
 * Browser assistant implementation for web
 */

import { get } from 'svelte/store';
import { readable } from 'svelte/store';
import { interpret, type Interpreter, type State } from 'xstate4';

import {
	cancelTranscribe,
	cancelTranscribeCommand,
	forceCleanup
} from '$lib/stores/transcriber.store';

import type { Minutes, Transcript, TranscriptFull } from '@repo/common';
import type {
	AssistantContext,
	AssistantEvents,
	LoadMediaEvent,
	StartUpdatingEvent,
	StopSummarizingEvent
} from '@repo/common/types/AssistantMachine';
import {
	BrowserRecordingController,
	flacRecordingDuration,
	webFlacRecordingStore
} from '@repo/browserlib';
import {
	machine,
	type ConvertToWavResult,
	type SummaryCompleteResult,
	type TranscribeCompleteResult
} from '@repo/sharedlib/src/machine/assistantMachine';
import { MediaFile } from '@repo/sharedlib/src/models/mediaFile';
import { captureFlowSelectedData } from '@repo/sharedlib/src/stores/captureFlow.store';
import { requestMediaDevice } from '@repo/sharedlib/src/stores/devices';
import { parseRawOutput } from '@repo/toolslib/src/utils/timecode';
import type { TranscriptOutputLineParsedTime } from '@repo/common';

/**
 * Parse bracket format transcript lines into display format
 * Input: "[00:00:00.100 - 00:00:03.000] text\n[00:00:03.100 - 00:00:05.000] more text"
 * Output: Array of TranscriptOutputLineParsedTime objects
 */
function parseBracketFormat(text: string): TranscriptOutputLineParsedTime[] {
	if (!text || text.trim().length === 0) {
		return [];
	}
	const lines = text.split('\n').filter(line => line.trim().length > 0);
	return lines.map((line, index) => {
		try {
			return parseRawOutput(line, index, true);
		} catch (error) {
			console.warn('[assistant] Failed to parse transcript line:', line, error);
			return null;
		}
	}).filter((line): line is TranscriptOutputLineParsedTime => line !== null);
}
import {
	getFullTranscript,
	refreshTranscriptsStore,
	removeFromSearchIndex,
	transcripts
} from '@repo/sharedlib/src/stores/transcripts';
import { taskStore } from '@repo/sharedlib/src/stores/task.store';
import { userAuthToken } from '@repo/sharedlib/src/stores/auth.store';
import { syncItems } from '@repo/sharedlib/src/adapter/clientSync';
import { goto } from '$app/navigation';
import { getHandler } from '$lib/handler';
import { tick } from 'svelte';

const formatDuration = (seconds: number) => {
	const safeSeconds = Math.max(0, seconds);
	const rounded = Math.round(safeSeconds * 1000) / 1000;
	const hours = Math.floor(rounded / 3600);
	const minutes = Math.floor((rounded % 3600) / 60);
	const secs = rounded % 60;
	const secsWhole = Math.floor(secs);
	const millis = Math.round((secs - secsWhole) * 1000);
	const secsStr = millis
		? `${secsWhole.toString().padStart(2, '0')}.${millis.toString().padStart(3, '0')}`
		: secsWhole.toString().padStart(2, '0');
	return `${hours.toString().padStart(2, '0')}:${minutes
		.toString()
		.padStart(2, '0')}:${secsStr}`;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const TRANSCRIBE_LOOP_INTERVAL_MS = 3000;

let assistantService: Interpreter<
	AssistantContext,
	any,
	AssistantEvents,
	{
		value: any;
		context: AssistantContext;
	}
>;
let assistantServiceStarted = false;
let recordingUnsubscribe: (() => void) | null = null;
let lastDuration = 0;
let lastFullTranscript = '';
const baseRecordingController = new BrowserRecordingController();

const attachRecordingListeners = () => {
	if (recordingUnsubscribe) {
		return;
	}
	lastDuration = 0;
	lastFullTranscript = '';
	recordingUnsubscribe = webFlacRecordingStore.subscribe((state) => {
		if (!assistantService) {
			return;
		}

		if (state.error) {
			assistantService.send({ type: 'RECORDING_ERROR', message: state.error });
			return;
		}

		// Only send progress if the state is recording and something meaningful has changed
		if (state.state !== 'recording') {
			return;
		}

		// Throttle duration updates to at least 1 second
		if (state.duration >= lastDuration + 1) {
			lastDuration = Math.floor(state.duration);
			assistantService.send({
				type: 'RECORDING_PROGRESS',
				data: {
					time: formatDuration(state.duration),
					size_kb: 0,
					bitrate_kbs: 0
				}
			});
		}

		if (state.fullTranscript !== lastFullTranscript) {
			lastFullTranscript = state.fullTranscript;
			assistantService.send({
				type: 'TRANSCRIPT_PROGRESS',
				partialTranscript: {
					output: parseBracketFormat(state.fullTranscript),
					duration: state.duration
				}
			});
		}
	});
};

const detachRecordingListeners = () => {
	if (recordingUnsubscribe) {
		recordingUnsubscribe();
		recordingUnsubscribe = null;
	}
	lastDuration = 0;
};

const assistantMachine = machine
	.withConfig({
		actions: {
			warmupModel: () => { },
			setActiveTranscript: (context) => {
				if (context.transcript) {
					transcripts.setActive(context.transcript.id);
				}
			},
			resetTranscribeProgress: (context) => {
				context.transcribeProgress = 0;
			},
			refreshSidebar: () => {
				refreshTranscriptsStore();
			},
			stopSummarizing: async (_context, event: StopSummarizingEvent): Promise<void> => {
				baseRecordingController.stopSummarizing(event.reason || '');
			},
			updateMinutes: (context, event: StartUpdatingEvent) => {
				const transcript = event?.transcript;
				const summary = event?.summary;
				if (transcript && summary) {
					transcripts.addMinutes(transcript.id, summary);
				} else {
					console.warn('Cannot update minutes; transcript or summary is missing', event);
				}
			},
			spawnRecordProcess: (context) => {
				void (async () => {
					if (!context.transcript?.id) {
						assistantService.send({
							type: 'RECORDING_ERROR',
							message: 'Missing transcript for recording'
						});
						return;
					}

					try {
						const recordPromise = baseRecordingController.startExistingRecording(context.transcript.id);
						context.recordingPromise = recordPromise;
						attachRecordingListeners();
					} catch (error) {
						const message = error instanceof Error ? error.message : 'Failed to start recording';
						assistantService.send({ type: 'RECORDING_ERROR', message });
					}
				})();
			}
		},
		services: {
			cleanUpAll: async () => {
				detachRecordingListeners();
				try {
					await baseRecordingController.abortRecordingAction('Cleanup requested');
				} catch (error) {
					console.warn('Cleanup error:', error);
				}
				forceCleanup();
				cancelTranscribe('Cleanup requested');
				await cancelTranscribeCommand();
			},
			createMedia: async (context): Promise<Transcript | undefined> => {
				const captureType = get(captureFlowSelectedData)?.captureType || 'web-recording';
				const newMediaFile = await MediaFile.createAutoNamed();

				if (!context.transcript) {
					context.transcript = await transcripts.createFromData(
						newMediaFile,
						context.calendarEvent,
						0,
						'ready',
						undefined,
						captureType
					);
				} else {
					context.transcript.file = newMediaFile;
					context.transcript.status = 'ready';
					context.transcript.data = {
						...context.transcript.data,
						captureType
					};
				}

				transcripts.setActive(context.transcript.id);
				transcripts.updateTranscript(context.transcript.id, context.transcript, true, true);
				return context.transcript;
			},
			loadMedia: async (context, event) => {
				const transcript = (event as LoadMediaEvent).transcript;
				if (transcript) {
					context.transcript = transcript;
					context.recordProgress = transcript.duration || 0;
					context.transcribeProgress = transcript.duration || 0;
					context.transcription = transcript.editedOutput ? [...transcript.editedOutput] : [];
					if (transcript.meeting?.minutes) {
						context.summary = transcript.meeting.minutes as Partial<Minutes>;
					}
					transcripts.setActive(transcript.id);
				}
			},
			getAvailableDevices: async () => {
				await requestMediaDevice();
			},
			stopRecordProcess: async () => {
				detachRecordingListeners();
				await baseRecordingController.stopRecordingAction('User stopped recording');
			},
			deleteRecordings: async (context): Promise<void> => {
				const $transcripts = get(transcripts);
				// Filter out undefined/null IDs to prevent errors
				const transcriptIds = (context.transcriptIds || []).filter(
					(id): id is string => typeof id === 'string' && id.length > 0
				);

				if (transcriptIds.length === 0) {
					console.warn('No valid transcript IDs provided for deletion');
					return;
				}

				const handler = await getHandler();

				const multiSelect = transcriptIds.length > 1;
				const firstName = $transcripts.idMap.get(transcriptIds[0])?.name || 'memory';

				// Show confirmation dialog using browser confirm
				const confirmed = window.confirm(
					multiSelect
						? `This will permanently delete ${transcriptIds.length} memories. This action cannot be undone.`
						: `Delete "${firstName}"? This action cannot be undone.`
				);

				if (!confirmed) {
					console.log('User cancelled deletion');
					return;
				}

				console.log('Deleting transcripts', transcriptIds);

				// Clear transcript context immediately to prevent state machine issues
				context.transcript = undefined;
				context.transcriptIds = undefined;

				// Set deleting state for UI feedback
				await Promise.all(
					transcriptIds.map((id) =>
						transcripts.updateTranscript(id, { isDeleting: true }, true, false).then(async () => {
							await tick();
						})
					)
				);

				const { messageModel, transcriptModel } = await handler.getDatabaseModels();

				// Delete transcripts from database
				try {
					const result = await transcriptModel.destroyRecords({ id: transcriptIds });
					if (result.rowsAffected !== transcriptIds.length) {
						console.warn(
							`Mismatch in delete operation: expected ${transcriptIds.length}, got ${result.rowsAffected}`
						);
					} else {
						console.log(`Successfully deleted ${result.rowsAffected} transcripts from database`);
					}
				} catch (error) {
					console.error('Failed to delete transcripts from database:', error);
				}

				// Delete messages, tasks, and audio blobs
				await Promise.all([
					...transcriptIds.map((id) => messageModel.deleteByTranscript(id)),
					...transcriptIds.map((id) => taskStore.deleteByMemory(id)),
					...transcriptIds.map((id) => baseRecordingController.deleteRecording(id, false)),
					...transcriptIds.map((id) => removeFromSearchIndex(id, 'deleteRecordings'))
				]);

				// Mark transcripts as deleted in the store
				await Promise.all(
					transcriptIds.map((id) =>
						transcripts.updateTranscript(id, { isDeleted: true, deletedAt: new Date() })
					)
				);

				// Sync deleted items to cloud
				try {
					const auth = get(userAuthToken);
					if (auth?.idToken && auth?.refreshToken) {
						const deletedTranscripts = transcriptIds.map((id) => ({ id }));
						console.log('Syncing deleted transcripts to cloud:', transcriptIds);
						await syncItems(
							{ transcript: deletedTranscripts },
							await handler.getDatabaseModels(),
							handler.getUserId() || '',
							auth,
							{
								direction: 'toItem',
								onProgress: (operation, itemsComplete, totalItems) => {
									console.log(`Syncing deleted transcripts: ${operation}. ${itemsComplete}/${totalItems}`);
								}
							}
						);
						console.log('Successfully synced deleted transcripts to cloud');
					}
				} catch (error) {
					console.error('Failed to sync deleted transcripts to cloud:', error);
				}

				// Navigate back to home
				await goto('/app/docs');
			},
			stopTranscription: async () => {
				cancelTranscribe('User stopped transcription');
				await cancelTranscribeCommand();
			},
			cancelTranscription: async () => {
				cancelTranscribe('User cancelled transcription');
				await cancelTranscribeCommand();
			},
			transcribeRecording: async (context): Promise<TranscribeCompleteResult> => {
				if (!context.transcript?.id) {
					throw new Error('Missing transcript for transcription');
				}

				const transcriptId = context.transcript.id;

				const shouldContinue = () => {
					if (!assistantService) {
						return false;
					}
					const snapshot = assistantService.getSnapshot();
					if (!snapshot) {
						return false;
					}
					if (!snapshot.matches('assistant.transcriber.transcribing')) {
						return false;
					}
					return snapshot.context.transcript?.id === transcriptId;
				};

				// The actual transcription updates are handled via TRANSCRIPT_PROGRESS
				// in the attachRecordingListeners subscription. 
				// This service loop just ensures the state machine stays in the 'transcribing' 
				// state until the recording is actually finished.
				while (shouldContinue() && (get(webFlacRecordingStore).state === 'recording' || get(webFlacRecordingStore).state === 'initializing')) {
					await sleep(1000);
				}

				// One last check for final transcript
				const finalTranscript = await getFullTranscript(transcriptId);
				if (!finalTranscript) {
					throw new Error('Transcript not found for transcription');
				}

				const finalDuration = Math.max(
					get(flacRecordingDuration),
					finalTranscript.duration || 0
				);
				const updatedTranscript = {
					...finalTranscript,
					status: 'transcribing'
				} as TranscriptFull;

				return {
					type: 'TRANSCRIBE_COMPLETE',
					transcript: updatedTranscript,
					transcription: finalTranscript.rawOutput || [],
					transcribeProgress: finalDuration,
					audioDetected: (finalTranscript.rawOutput || []).length > 0,
					incrementalIndex: context.incrementalIndex,
					startSummary: false
				};
			},
			finalTranscribe: async (
				context
			): Promise<Omit<TranscribeCompleteResult, 'incrementalIndex'>> => {
				if (!context.transcript?.id) {
					throw new Error('Missing transcript for final transcription');
				}

				await baseRecordingController.startTranscribing(context.transcript.id);

				const transcript = await getFullTranscript(context.transcript.id);
				if (!transcript) {
					throw new Error('Transcript not found after final transcription');
				}

				return {
					type: 'TRANSCRIBE_COMPLETE',
					transcript,
					transcription: transcript.rawOutput || [],
					transcribeProgress: transcript.duration || 0,
					audioDetected: (transcript.rawOutput || []).length > 0,
					startSummary: !!context.pendingFinalSummarize
				};
			},
			summarize: async (context): Promise<SummaryCompleteResult> => {
				if (!context.transcript?.id) {
					throw new Error('Missing transcript for summarization');
				}

				await baseRecordingController.startSummarizing(context.transcript.id);

				const transcript = await getFullTranscript(context.transcript.id);
				return {
					type: 'SUMMARY_COMPLETE',
					summary: transcript?.meeting?.minutes ?? undefined
				};
			},
			finalSummarize: async (context): Promise<SummaryCompleteResult> => {
				if (!context.transcript?.id) {
					throw new Error('Missing transcript for final summarization');
				}

				await baseRecordingController.startSummarizing(context.transcript.id);

				const transcript = await getFullTranscript(context.transcript.id);
				return {
					type: 'SUMMARY_COMPLETE',
					summary: transcript?.meeting?.minutes ?? undefined
				};
			},
			convertToWav: async (): Promise<ConvertToWavResult> => {
				return {
					type: 'CONVERT_TO_WAV_COMPLETE',
					cancelFinalTranscribe: false
				};
			}
		}
	})
	.withContext({
		...machine.context
	});

// @ts-expect-error complex xstate types
assistantService = interpret(assistantMachine);
if (typeof window !== 'undefined') {
	assistantService.start();
	assistantServiceStarted = true;

	// Register the assistant event sender callback for BrowserRecordingController
	// This enables browserlib to send events to the assistant state machine without circular imports
	BrowserRecordingController.setAssistantEventSender((event) => {
		assistantService.send(event);
	});
}

export function startAssistantService() {
	if (!assistantServiceStarted) {
		assistantService.start();
		assistantServiceStarted = true;
	}
}

export function stopAssistantService() {
	if (assistantServiceStarted) {
		assistantService.stop();
		assistantServiceStarted = false;
		detachRecordingListeners();
	}
}

const assistantState = readable<State<AssistantContext, AssistantEvents>>(
	assistantService.state,
	(set) => {
		return assistantService.subscribe((state: any) => {
			if (state.changed) {
				set(state);
			}
		}).unsubscribe;
	}
);

const assistantSend = (event: AssistantEvents | AssistantEvents[]) => {
	if (!assistantServiceStarted && typeof window !== 'undefined') {
		startAssistantService();
	}
	return assistantService.send(event);
};

// Compatibility helpers for the existing web API surface.
export const activateAssistant = async () => {
	startAssistantService();
};

export const deactivateAssistant = async () => {
	stopAssistantService();
};

export const sendQuery = async (_query: string) => {
	console.warn('sendQuery not implemented in web');
};

export { assistantSend, assistantService, assistantState };

if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		stopAssistantService();
	});
}
